/**
 * @file     ChoiceChips.tsx
 * @purpose  A labelled single-choice chip group for the family 7 tools
 *           (amortization, payment frequency, answer bands), built on the
 *           step-flow chip so every tool shares one chip.
 */
'use client';

import { useId } from 'react';
import Box from '@mui/material/Box';
import { FlowChip } from '@/features/lead-flows/components/FlowChip';
import { hubColors } from '@/components/hub/hubKitStyles';

type ChoiceChipsProps<T extends string> = {
  label: string;
  value: T | '' | null | undefined;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  hint?: string;
};

export function ChoiceChips<T extends string>({ label, value, options, onChange, hint }: ChoiceChipsProps<T>) {
  const labelId = useId();
  return (
    <Box role="group" aria-labelledby={labelId} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box id={labelId} component="p" sx={{ m: 0, fontSize: 14, fontWeight: 500, color: hubColors.textPrimary }}>
        {label}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {options.map((option) => (
          <FlowChip key={option.value} selected={value === option.value} onClick={() => onChange(option.value)}>
            {option.label}
          </FlowChip>
        ))}
      </Box>
      {hint ? (
        <Box component="p" sx={{ m: 0, fontSize: 13, lineHeight: 1.5, color: hubColors.textSecondary }}>
          {hint}
        </Box>
      ) : null}
    </Box>
  );
}
